import { useQuery } from "@tanstack/react-query";
import { api } from "../services/api";
import { TrackingMetadata } from "../utils/tracking";

export interface PublicCheckoutProductPrice {
  stripePriceId: string;
  unitAmount: number;
  currency: string;
  type: "one_time" | "subscription";
  recurring?: { interval: string; intervalCount: number };
  commitment?: { interval: string; intervalCount: number };
}

export interface PublicCheckoutProduct {
  id: string;
  name: string;
  description?: string;
  avatar?: { type: string; value: string };
  branding?: { promotionalBadge?: string; coverImageUrl?: string };
  highlights?: {
    included: Array<{ text: string }>;
    notIncluded: Array<{ text: string }>;
  };
  /** @deprecated Use `prices` array and resolve by currency instead */
  price: {
    stripePriceId: string;
    unitAmount: number;
    currency: string;
    type: "one_time" | "subscription";
    recurring?: { interval: string; intervalCount: number };
  };
  prices: PublicCheckoutProductPrice[];
  commitment?: { interval: string; intervalCount: number };
  deliveryMethod?: string;
  serviceConfig?: { type: string; duration: number; calendarLink?: string };
  postPaymentMessage?: string;
}

export interface PublicCheckoutDetails {
  paymentLinkId: string;
  mode: "subscription" | "payment";
  products: PublicCheckoutProduct[];
  business: {
    name?: string;
    logo?: string;
    /** "workspace" when a team workspace sells, "coach" when a coach sells on his own. */
    kind?: "workspace" | "coach";
    /** The business mark (workspace logo, else the payment account's icon); drawn square. */
    icon?: string;
    /** The coach's own photo; drawn round. Never sent for a team. */
    photo?: string;
    supportEmail?: string;
    brandColor?: string;
    secondaryColor?: string;
  };
  stripeAccountId: string;
  creatorId: string;
  options: {
    collectBillingAddress: boolean;
    collectPhoneNumber: boolean;
    allowPromotionCodes: boolean;
  };
  availableCurrencies?: string[];
  defaultCurrency?: string;
}

export interface CheckoutPaymentResult {
  clientSecret: string;
  mode: "subscription" | "payment";
  amount: number;
  currency: string;
  subscriptionId?: string;
}

export interface CreateCheckoutPaymentData {
  email: string;
  name?: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postal_code: string;
    country: string;
  };
  promotionCode?: string;
  currency?: string;
  metadata?: TrackingMetadata;
}

export function useCheckoutDetails(paymentLinkId: string | undefined) {
  return useQuery({
    queryKey: checkoutKeys.details(paymentLinkId!),
    queryFn: async () => {
      const response = await api.get<PublicCheckoutDetails>(
        `/payment-links/${paymentLinkId}/checkout-details`,
      );
      return response.data;
    },
    enabled: !!paymentLinkId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      // A closed or unknown link does not reopen on retry; show it at once.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 400) return false;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

export async function createCheckoutPayment(
  paymentLinkId: string,
  data: CreateCheckoutPaymentData,
): Promise<CheckoutPaymentResult> {
  const response = await api.post<CheckoutPaymentResult>(
    `/payment-links/${paymentLinkId}/create-payment`,
    data,
  );
  return response.data;
}

export interface CheckPromoCodeResult {
  valid: boolean;
  discount: {
    type: "percent" | "amount";
    percentOff?: number;
    amountOff?: number;
    currency?: string;
  };
  discountedAmount: number;
  originalAmount: number;
}

export async function checkPromoCode(
  paymentLinkId: string,
  code: string,
  currency?: string,
): Promise<CheckPromoCodeResult> {
  const response = await api.post<CheckPromoCodeResult>(
    `/payment-links/${paymentLinkId}/check-promo`,
    { code, currency },
  );
  return response.data;
}

export async function trackPaymentLinkVisit(
  paymentLinkId: string,
  metadata: TrackingMetadata,
) {
  return api
    .post(`/payment-links/${paymentLinkId}/track-visit`, metadata)
    .catch(() => {});
}

export const checkoutKeys = {
  details: (linkId: string) => ["checkout", linkId] as const,
};
